import React from "react";
import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import TrustBar from "../components/TrustBar";
import BrandTicker from "../components/BrandTicker";
import BrandCard from "../components/BrandCard";
import Values from "../components/Values";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, TrendingUp } from "lucide-react";

const Home = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBrands = async () => {
        try {
            const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
            const response = await axios.get(`${BACKEND_URL}/api/brands`);
            setBrands(response.data);
        } catch (error) {
            console.error("Failed to fetch brands", error);
            toast.error("Could not load brand portfolio");
        } finally {
            setLoading(false);
        }
    };
    
    fetchBrands();
  }, []);

  return (
    <div className="bg-dreibach-black min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80&w=2000" 
            alt="Logistics Warehouse" 
            className="w-full h-full object-cover opacity-30 grayscale"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-dreibach-black via-dreibach-black/60 to-transparent" />
        </div>

        <div className="container relative z-10 px-6 text-center">
          <div className="inline-flex items-center gap-2 py-1 px-4 border border-white/20 bg-black/40 backdrop-blur-md text-white/80 text-xs font-bold tracking-[0.2em] uppercase mb-8">
             <span className="w-2 h-2 rounded-full bg-dreibach-teal animate-pulse"></span>
             Global Logistics • Est. 2021
          </div>
          
          <h1 className="heading-xl mb-8 text-white max-w-5xl mx-auto">
            The Engine Behind<br/>
            <span className="text-dreibach-teal">Modern Living.</span>
          </h1>
          
          <p className="body-lg text-gray-300 max-w-2xl mx-auto mb-12 font-light leading-relaxed">
            From global sourcing to same-day dispatch. We curate, manage, and deliver the brands that power 1,000,000+ UK homes.
          </p>
          
          <div className="flex flex-col md:flex-row gap-6 justify-center">
            <Link to="/brands">
                <Button size="lg" className="bg-white text-black hover:bg-gray-200 uppercase font-bold tracking-widest px-10 py-7 text-sm rounded-none transition-transform hover:-translate-y-1">
                Explore Our Brands
                </Button>
            </Link>
            {/* <Link to="/infrastructure">
                <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-black uppercase font-bold tracking-widest px-10 py-7 text-sm rounded-none transition-transform hover:-translate-y-1">
                Our Infrastructure
                </Button>
            </Link> */}
          </div>
        </div>
      </section>

      <TrustBar />
      <BrandTicker />

      {/* Ecosystem Section */}
      <section className="py-32 bg-dreibach-black relative">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8">
            <div className="max-w-xl">
               <h2 className="heading-lg text-white mb-6">A House of<br/>Brands</h2>
               <p className="body-lg text-gray-400">
                  We don't just distribute products; we build brands. From the garden sanctuary of Pelle & Sol to the family essentials of Yum Yum, our portfolio covers every corner of daily life.
               </p>
            </div>
            <Link to="/brands" className="hidden md:flex items-center gap-3 text-dreibach-teal uppercase font-bold tracking-wider text-sm hover:text-white transition-colors group">
               View Full Portfolio <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading ? (
                <div className="col-span-3 flex justify-center py-12">
                    <Loader2 className="w-8 h-8 text-dreibach-teal animate-spin" />
                </div>
            ) : (
                brands.map((brand, idx) => (
                  <BrandCard key={idx} brand={brand} />
                ))
            )}
          </div>
          
           <div className="mt-12 text-center md:hidden">
            <Link to="/brands" className="inline-flex items-center gap-2 text-dreibach-teal uppercase font-bold tracking-wider text-sm">
               View Full Portfolio <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Logistics Authority */}
      <section className="py-32 bg-dreibach-navy relative overflow-hidden">
         {/* Background pattern */}
         <div className="absolute inset-0 opacity-5 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '50px 50px' }}></div>
         
         <div className="container mx-auto px-6 relative z-10">
            <div className="grid md:grid-cols-2 gap-20 items-center">
               <div className="order-2 md:order-1">
                  <div className="relative border border-white/10 p-4 bg-white/5 backdrop-blur-sm">
                     <img 
                        src="https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&q=80&w=1000" 
                        alt="Northampton Super-Hub" 
                        className="w-full h-auto grayscale relative z-10 border border-white/10 shadow-2xl"
                     />
                     <div className="absolute -bottom-6 -right-6 bg-dreibach-black p-8 border border-white/10 z-20 shadow-xl">
                        <p className="text-dreibach-teal font-black text-5xl">2025</p>
                        <p className="text-gray-500 uppercase text-xs tracking-[0.2em] font-bold mt-2">Northampton Super-Hub Opened</p>
                     </div>
                  </div>
               </div>
               
               <div className="order-1 md:order-2">
                  <h2 className="heading-lg text-white mb-8">Logistics Authority</h2>
                  <p className="body-lg text-gray-300 mb-8 font-light leading-relaxed">
                     Powered by our new Northampton facility and Multi-Channel Shipping technology, we execute logistics at the speed of demand. We are the bridge between global manufacturing and the British consumer.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-8 mb-10">
                      <div>
                          <h4 className="text-3xl font-black text-white mb-1">50</h4>
                          <p className="text-xs uppercase tracking-widest text-dreibach-teal font-bold">Orders Per Minute</p>
                      </div>
                      <div>
                          <h4 className="text-3xl font-black text-white mb-1">24h</h4>
                          <p className="text-xs uppercase tracking-widest text-dreibach-teal font-bold">Dispatch Time</p>
                      </div>
                  </div>

                  {/* <Link to="/infrastructure">
                     <Button className="bg-white text-black hover:bg-dreibach-teal hover:text-white uppercase font-bold tracking-widest px-8 py-6 rounded-none transition-colors">
                        Explore Infrastructure
                     </Button>
                  </Link> */}
               </div>
            </div>
         </div>
      </section>
      
      <Values />
    </div>
  );
};

export default Home;
